import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, CheckIcon, EyeIcon, LockIcon, SendIcon, XIcon } from 'lucide-react';
import { BORROWER_NAME } from '../../data/conditions';
import { isResolved } from '../../utils/conditions';
import type { Condition } from '../../types/conditions';
interface SendConditionsDialogProps {
  open: boolean;
  conditions: Condition[];
  onClose: () => void;
  onSend: (ids: string[]) => void;
}

/** Preview of exactly what the borrower receives, with per-item opt-out before sending. */
export function SendConditionsDialog({
  open,
  conditions,
  onClose,
  onSend
}: SendConditionsDialogProps) {
  const outstanding = conditions.filter(c => !isResolved(c.status));
  const borrowerItems = outstanding.filter(c => c.borrowerVisible);
  const internalCount = outstanding.length - borrowerItems.length;
  const [selected, setSelected] = useState<string[]>([]);
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (open) {
      setSelected(borrowerItems.map(c => c.id));
      setSent(false);
    }
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  function toggle(id: string) {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  }
  return <AnimatePresence data-id="element-299" data-mp="37seq4xa9m1z8">
      {open && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-300" data-mp="37tg357aucyjl">
          <motion.div className="absolute inset-0 bg-slate-900/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} aria-hidden="true" data-id="element-301" data-mp="32zllsaipkvno" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="send-conditions-title" initial={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.16
      }} className="relative flex max-h-[88vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl" data-id="element-302" data-mp="301677zszllu7">
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-6 py-4" data-id="element-303" data-mp="2h3vcmsiavgl3">
              <div data-id="element-304" data-mp="2q26zqkjyjw8s">
                <h2 id="send-conditions-title" className="text-base font-semibold text-charcoal" data-id="element-305" data-mp="2q1np7own0a4s">
                  {sent ? 'Conditions sent' : 'Preview borrower email'}
                </h2>
                <p className="mt-0.5 text-sm text-slate-500" data-id="element-306" data-mp="5chla8n62cgm">
                  {sent ? `${BORROWER_NAME} will see these in their portal and inbox.` : 'This is exactly what the borrower will receive. Uncheck anything you don’t want to send.'}
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-307" data-mp="1pv1utpxmcboj">
                <XIcon className="h-5 w-5" data-id="element-308" data-mp="1c3n93bcbtwds" />
              </button>
            </header>

            {sent ? <div className="flex flex-col items-center px-6 py-12 text-center" data-id="element-309" data-mp="171xyfwt0nkq8">
                <CheckCircle2Icon className="h-10 w-10 text-brand" aria-hidden="true" data-id="element-310" data-mp="125h9j0dzt2c3" />
                <p className="mt-3 text-sm font-semibold text-slate-900" data-id="element-311" data-mp="x64psoif4bdq">
                  {selected.length} {selected.length === 1 ? 'condition' : 'conditions'} sent to {BORROWER_NAME}
                </p>
                <p className="mt-1 text-sm text-slate-500" data-id="element-312" data-mp="2rkff0y2xd7k1">Each condition’s activity timeline has been updated.</p>
              </div> : <div className="flex-1 overflow-y-auto bg-slate-50 px-6 py-5" data-id="element-313" data-mp="2zi7mlj6ts0uv">
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-id="element-314" data-mp="34idgz74hc1gn">
                  <dl className="grid grid-cols-[64px_1fr] gap-y-1 border-b border-slate-200 px-5 py-3 text-sm" data-id="element-315" data-mp="2plcr795umgkt">
                    <dt className="text-slate-500" data-id="element-316" data-mp="2un1zih1osg57">To</dt>
                    <dd className="font-medium text-slate-900" data-id="element-317" data-mp="2gsj5asvwcixz">{BORROWER_NAME}</dd>
                    <dt className="text-slate-500" data-id="element-318" data-mp="2lksta3dgb8xm">Subject</dt>
                    <dd className="text-slate-900" data-id="element-319" data-mp="2lm3v3xnc66m3">A few items needed for your loan</dd>
                  </dl>
                  <div className="px-5 py-4 text-sm text-slate-700" data-id="element-320" data-mp="2buhbpe0uh189">
                    <p data-id="element-321" data-mp="2bur3n75g9igl">Hi Michael and Sarah,</p>
                    <p className="mt-2" data-id="element-322" data-mp="1xu6jo7nstgli">
                      To keep your loan moving, we need the following. You can upload everything securely in your
                      borrower portal.
                    </p>

                    {borrowerItems.length === 0 ? <p className="mt-4 rounded-md border border-dashed border-slate-300 px-4 py-3 text-slate-500" data-id="element-323" data-mp="1j9uap7ddem3s">
                        No outstanding conditions are visible to the borrower.
                      </p> : <ul className="mt-4 space-y-2" data-id="element-324" data-mp="1n8jlqtaxrrk9">
                        {borrowerItems.map(c => {
                  const checked = selected.includes(c.id);
                  return <li key={c.id} data-id="element-325" data-mp="1x6zbosy88us6">
                              <label className={`flex cursor-pointer gap-3 rounded-md border px-3 py-2.5 transition-colors ${checked ? 'border-brand/30 bg-brand/[0.04]' : 'border-slate-200 bg-white opacity-60'}`} data-id="element-326" data-mp="2az5tejh139tq">
                                <input type="checkbox" checked={checked} onChange={() => toggle(c.id)} className="sr-only" data-id="element-327" data-mp="2fzl5z8thkdan" />
                                <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white'}`} aria-hidden="true" data-id="element-328" data-mp="1mkhdharzj154">
                                  {checked && <CheckIcon className="h-3 w-3" data-id="element-329" data-mp="25dbj3e3pxgwo" />}
                                </span>
                                <span className="min-w-0" data-id="element-330" data-mp="2aal7cqfdfmix">
                                  <span className={`block font-medium text-slate-900 ${checked ? '' : 'line-through'}`} data-id="element-331" data-mp="ayiq7mu7z48f">
                                    {c.name}
                                  </span>
                                  <span className="block text-slate-600" data-id="element-332" data-mp="8duilxgm5x9x">{c.borrowerDescription ?? c.description}</span>
                                </span>
                              </label>
                            </li>;
                })}
                      </ul>}

                    <p className="mt-4" data-id="element-333" data-mp="3pvnhb7xp4ivi">Thank you,</p>
                    <p data-id="element-334" data-mp="rc6rdi3m554z">The Mortgage Depot team</p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500" data-id="element-335" data-mp="mdlqrhkg8jg0">
                  <span className="inline-flex items-center gap-1" data-id="element-336" data-mp="he94oz1ejgeh">
                    <EyeIcon className="h-3.5 w-3.5 text-amber-700" aria-hidden="true" data-id="element-337" data-mp="hcfz7n2iz282" />
                    Only conditions marked “Borrower can see” are included
                  </span>
                  {internalCount > 0 && <span className="inline-flex items-center gap-1" data-id="element-338" data-mp="2y2wl2xg0iu69">
                      <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-339" data-mp="2y4zi41fl1nkh" />
                      {internalCount} internal {internalCount === 1 ? 'condition is' : 'conditions are'} hidden
                    </span>}
                </div>
              </div>}

            <footer className="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-3" data-id="element-340" data-mp="s3ez6hpj9w0f">
              {sent ? <span data-id="element-341" data-mp="1kq81h4ikn3cj" /> : <p className="text-sm text-slate-500" data-id="element-342" data-mp="1krj7lazhon6t">
                  {selected.length} of {borrowerItems.length} selected
                </p>}
              <div className="flex gap-2" data-id="element-343" data-mp="1b0oi7xqy57h5">
                {sent ? <button type="button" onClick={onClose} className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-344" data-mp="1ayc45vob7h9d">
                    Done
                  </button> : <>
                    <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-345" data-mp="2dt0vxz5qemjb">
                      Cancel
                    </button>
                    <button type="button" disabled={selected.length === 0} onClick={() => {
                onSend(selected);
                setSent(true);
              }} className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-346" data-mp="1uu6cwdz8izxc">
                      <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-347" data-mp="24n4sqmgurhab" />
                      Send to borrower
                    </button>
                  </>}
              </div>
            </footer>
          </motion.div>
        </div>}
    </AnimatePresence>;
}